import { DIALOG_DATA } from '@angular/cdk/dialog';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { CurrentScopeFacade } from '@principle-theorem/ng-principle-shared';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import { CustomReport } from '@principle-theorem/principle-core';
import { ICustomReportColumn } from '@principle-theorem/principle-core/interfaces';
import {
  CanBeChartedProperty,
  IResolvedColumnProperty,
  ReportBuilderDataSourceInstance,
  ResolvedColumns,
} from '@principle-theorem/reporting';
import { of, Subject } from 'rxjs';
import { ReportBuilderDataPointSelectorComponent } from '../../report-builder-data-point-selector/report-builder-data-point-selector.component';
import { ReportBuilderManageChartBloc } from '../report-builder-add-chart-dialog/report-builder-manage-chart-bloc';
import { ReportBuilderResolvedColumnLabelComponent } from '../report-builder-resolved-column-label/report-builder-resolved-column-label.component';

export interface IReportBuilderColumnsDialogRequest {
  dataSource: ReportBuilderDataSourceInstance;
  columns: ICustomReportColumn[];
}

export interface IReportBuilderColumnsDialogResponse {
  columns: ICustomReportColumn[];
}

@Component({
  selector: 'pr-report-builder-columns-dialog',
  templateUrl: './report-builder-columns-dialog.component.html',
  styleUrls: ['./report-builder-columns-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    MatTooltip,
    NgDialogModule,
    ReportBuilderDataPointSelectorComponent,
    ReportBuilderResolvedColumnLabelComponent,
  ],
})
export class ReportBuilderColumnsDialogComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  columns: IResolvedColumnProperty[] = [];
  bloc: ReportBuilderManageChartBloc;

  constructor(
    @Inject(DIALOG_DATA) private _data: IReportBuilderColumnsDialogRequest,
    private _dialogRef: MatDialogRef<
      ReportBuilderColumnsDialogComponent,
      IReportBuilderColumnsDialogResponse
    >,
    private _scope: CurrentScopeFacade
  ) {
    this.bloc = new ReportBuilderManageChartBloc(
      this._scope.currentBrand$,
      of(this._data.dataSource),
      [],
      this._onDestroy$,
      {},
      [],
      (column) => {
        if (column.id.endsWith('.count')) {
          return false;
        }
        return column.measure.metadata.availableAsColumn !== false;
      }
    );
    this.columns = ResolvedColumns.toColumns(
      this._data.dataSource,
      this._data.columns
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
  }

  addColumn(column: CanBeChartedProperty): void {
    this.bloc.dataPointSearchCtrl.reset(undefined, { emitEvent: false });

    const newColumn = CustomReport.column(column.metadata.id);
    const resolvedColumn = ResolvedColumns.resolveColumn(
      this._data.dataSource,
      newColumn
    );
    if (!resolvedColumn) {
      return;
    }
    this.columns = [...this.columns, resolvedColumn];
  }

  removeColumn(column: IResolvedColumnProperty): void {
    this.columns = this.columns.filter(
      (selectedColumn) =>
        selectedColumn.definition.uid !== column.definition.uid
    );
  }

  submit(): void {
    this._dialogRef.close({
      columns: this.columns.map((column) => column.definition),
    });
  }
}
